// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useRef } from "react";
import { KeyCode } from "../../internal/keycode";
import { filterOptions, isInteractive, isGroupInteractive, isGroup } from "../../internal/components/option/utils/filter-options";
import { useDebounceCallback } from "../../internal/hooks/use-debounce-callback";
export var isChar = function(keyCode) {
    return [
        0,
        KeyCode.enter,
        KeyCode.space,
        KeyCode.tab
    ].indexOf(keyCode) === -1;
};
export var isRepeatedChar = function(str) {
    return str.split("").every(function(c) {
        return c === str[0];
    });
};
function findMatchingOption(options, searchText, currentHighlight, useInteractiveGroups) {
    var interactivityCheck = useInteractiveGroups ? isGroupInteractive : isInteractive;
    var filter = function(searchText) {
        return filterOptions(options, searchText, true).filter(function(option) {
            return interactivityCheck(option);
        });
    };
    var matchingOptions = filter(searchText);
    if (matchingOptions.length === 1) {
        return matchingOptions[0];
    }
    // Hit the same char over and over, we assume they want to cycle through
    if (currentHighlight && searchText.length > 1 && isRepeatedChar(searchText)) {
        var matchingOptions_1 = filter(searchText[0]);
        if (matchingOptions_1.length > 0) {
            //handling OptionGroups when useInteractiveGroups is set
            var active = isGroup(currentHighlight) ? matchingOptions_1.map(function(_a) {
                var option = _a.option;
                return option;
            }).indexOf(currentHighlight) : matchingOptions_1.map(function(_a) {
                var option = _a.option;
                return option.value;
            }).indexOf(currentHighlight.value);
            // Pick the next thing (if something with this prefix wasn't selected
            // we'll end up with the first option)
            active += 1;
            active = active % matchingOptions_1.length;
            return matchingOptions_1[active];
        }
    }
    // We have multiple things that start with this prefix.  Based on the
    // behavior of native select, this is considered after the repeated case
    if (matchingOptions.length > 0) {
        return matchingOptions[0];
    }
    return null;
}
export function useNativeSearch(_a) {
    var isEnabled = _a.isEnabled, options = _a.options, highlightOption = _a.highlightOption, highlightedOption = _a.highlightedOption, useInteractiveGroups = _a.useInteractiveGroups;
    var value = useRef("");
    var delayedResetValue = useDebounceCallback(function() {
        return value.current = "";
    }, 500);
    return function(event) {
        if (!isEnabled) {
            return;
        }
        var charCode = event.charCode;
        if (!isChar(charCode)) {
            return;
        }
        delayedResetValue();
        var newValue = value.current + String.fromCharCode(charCode);
        value.current = newValue;
        var nextHighlight = findMatchingOption(options, newValue, highlightedOption, useInteractiveGroups);
        if (nextHighlight) {
            highlightOption(nextHighlight);
        }
    };
} //# sourceMappingURL=use-native-search.js.map
